import { isEqual } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { SetToothFullHeadMPRSliceEditorStateReq } from '@/shared/api/protocol-ts/api/core/svc_report_pb';
import { AnnotationsLayer } from '@/shared/api/protocol-ts/model/dto_common_image_annotations_pb';
import { MedicalImageViewOptions } from '@/shared/api/protocol-ts/model/dto_common_image_view_options_pb';
import { toastErrors } from '@/shared/config';
import {
  MedicalImageRender,
  type FullHeadMPRAnnotationsPreview,
  type FullHeadMPRAnnotationTarget,
  type FullHeadMPRImageOptions,
  type FullHeadMPRImageOptionsTarget,
  type FullHeadMPRProjection,
  type FullHeadMPRSession,
  type FullHeadMPRUIEvents,
  type FullHeadMPRViewSnapshot,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import {
  captureAnnotationsOverlay,
  composeAnnotationsOverlay,
} from '@/shared/graphics/RenderComponents/AnnotationsOverlay';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { toastCaller } from '@/shared/ui';

import { formatAnnotations, medicalImageTitles } from '@/entities/assets';
import {
  reportsModel,
  transformViewOptions,
  useCheckReportSignature,
} from '@/entities/reports';

import {
  isSameFullHeadMprAnnotationPlane,
  isSameFullHeadMprPlane,
  makeFullHeadMprEditorStateRequest,
  makeFullHeadMprPointTransform,
  type FullHeadMprOriginalSlice,
} from '@/features/MPR';

import {
  FULL_HEAD_MPR_SESSION_PROJECTIONS,
  FULL_HEAD_MPR_PROJECTION_TO_SERIES,
  TOOTH_BASIS_SERIES_TO_GROUP,
  type FullHeadMprEditorTarget,
} from '../config';
import {
  makeFullHeadMprEditorTarget,
  makeFullHeadMprSliceViewOptions,
  transformFullHeadMprGraphicsAnnotations,
} from '../lib';

const PAGE_EDITOR_VIEWER = {
  captureScreenshot: (projection: FullHeadMPRProjection) =>
    MedicalImageRender.captureFullHeadMPRScreenshot(projection),
  getActivePane: () => MedicalImageRender.getFullHeadMPRActivePane(),
  getPaneElement: (projection: FullHeadMPRProjection) =>
    MedicalImageRender.getFullHeadMPRPaneElement(projection) ?? null,
  getPaneSurface: (projection: FullHeadMPRProjection) =>
    MedicalImageRender.getFullHeadMPRPaneSurface(projection) ?? null,
  getVisibleProjections: () =>
    MedicalImageRender.getFullHeadMPRVisibleProjections() ?? [],
  getViewSnapshot: (projection: FullHeadMPRProjection) =>
    MedicalImageRender.getFullHeadMPRViewSnapshot(projection) ?? null,
  isViewReady: (projection: FullHeadMPRProjection) =>
    MedicalImageRender.isFullHeadMPRViewReady(projection) ?? false,
  setAnnotationTarget: (
    projection: FullHeadMPRProjection,
    target: FullHeadMPRAnnotationTarget | null,
  ) => MedicalImageRender.setFullHeadMPRAnnotationTarget(projection, target),
  setImageOptions: (
    projection: FullHeadMPRProjection,
    options: FullHeadMPRImageOptions | null,
  ) => MedicalImageRender.setFullHeadMPRImageOptions(projection, options),
  setImageOptionsTarget: (
    projection: FullHeadMPRProjection,
    target: FullHeadMPRImageOptionsTarget | null,
  ) => MedicalImageRender.setFullHeadMPRImageOptionsTarget(projection, target),
};

type PendingImageOptions = {
  options: FullHeadMPRImageOptions;
  targetID: string;
};

type PendingEdit = {
  projection: FullHeadMPRProjection;
  target: FullHeadMprEditorTarget;
  annotations?: AnnotationsLayer[];
  options?: FullHeadMPRImageOptions;
  // Every projection of one brightness/contrast gesture (sync mirrors the
  // options onto the other panes and the engine reports each separately).
  optionsByProjection?: Partial<
    Record<FullHeadMPRProjection, PendingImageOptions>
  >;
};

type UseFullHeadMprEditorParams = {
  isReady: boolean;
  toothID: string;
  // The "Only this slice" override reshapes the session without retargeting
  // the editor, so a drawing made then would land on the series record.
  isAnnotationsLocked?: boolean;
  original?: FullHeadMprOriginalSlice | null;
  session?: FullHeadMPRSession | null;
};

export const useFullHeadMprEditor = ({
  isAnnotationsLocked = false,
  isReady,
  toothID,
  original,
  session,
}: UseFullHeadMprEditorParams) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { checkReportSignature } = useCheckReportSignature();
  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const slices = useAppSelector(
    reportsModel.selectors.selectFullHeadMprSlicesByToothID(toothID),
  );
  const loading = useAppSelector(
    reportsModel.selectors.selectFullHeadMprLoading(report?.ID ?? ''),
  );
  const isSaving = useAppSelector(
    reportsModel.selectors.selectIsFullHeadMprWritePending(toothID),
  );
  const viewer = session ?? PAGE_EDITOR_VIEWER;
  const [snapshots, setSnapshots] = useState<
    Partial<Record<FullHeadMPRProjection, FullHeadMPRViewSnapshot>>
  >({});
  const [readyProjections, setReadyProjections] = useState<
    FullHeadMPRProjection[]
  >([]);
  const [previews, setPreviews] = useState<
    Partial<Record<FullHeadMPRProjection, FullHeadMPRAnnotationsPreview>>
  >({});
  const [pending, setPending] = useState<PendingEdit | null>(null);
  const [isCheckingSignature, setIsCheckingSignature] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [revision, setRevision] = useState(0);
  const targetsRef = useRef<
    Partial<Record<FullHeadMPRProjection, FullHeadMprEditorTarget>>
  >({});
  const pendingRef = useRef<PendingEdit | null>(null);
  const appliedImageOptionsRef = useRef<
    Partial<Record<FullHeadMPRProjection, string>>
  >({});
  const mountedRef = useRef(false);
  const downloadingRef = useRef(false);
  const hasLoadedTooth = isReady && !!toothID && loading === 'succeeded';
  const canEditAnnotations =
    hasLoadedTooth &&
    !isAnnotationsLocked &&
    !isSaving &&
    !pending &&
    !!report?.YourPermissions?.CanChangeReportAssets &&
    !!report.YourPermissions.CanChangeToothAssets;
  const canEditImageOptions =
    hasLoadedTooth &&
    !isSaving &&
    !pending &&
    !!report?.YourPermissions?.CanChangeReportAssets;
  const latestRef = useRef({
    canEditAnnotations,
    canEditImageOptions,
    checkReportSignature,
    slices,
  });
  latestRef.current = {
    canEditAnnotations,
    canEditImageOptions,
    checkReportSignature,
    slices,
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    targetsRef.current = {};
    appliedImageOptionsRef.current = {};
    setSnapshots({});
    setReadyProjections([]);
    setPreviews({});
    pendingRef.current = null;
    setPending(null);
    setIsCheckingSignature(false);
    if (!isReady || !toothID) {
      return;
    }

    const updateSnapshots = () => {
      const visibleProjections = viewer.getVisibleProjections();
      const next = visibleProjections.reduce<
        Partial<Record<FullHeadMPRProjection, FullHeadMPRViewSnapshot>>
      >((result, projection) => {
        const snapshot = viewer.getViewSnapshot(projection);
        return snapshot ? { ...result, [projection]: snapshot } : result;
      }, {});
      const ready = visibleProjections.filter((projection) =>
        viewer.isViewReady(projection),
      );
      setSnapshots((current) => (isEqual(current, next) ? current : next));
      setReadyProjections((current) =>
        isEqual(current, ready) ? current : ready,
      );
    };
    const finish = (edit: PendingEdit, restore: boolean) => {
      if (pendingRef.current !== edit) {
        return;
      }
      pendingRef.current = null;
      if (!mountedRef.current) {
        return;
      }
      if (
        restore &&
        edit.annotations &&
        targetsRef.current[edit.projection]?.id === edit.target.id
      ) {
        void viewer.setAnnotationTarget(edit.projection, null);
      }
      setPending(null);
      setIsCheckingSignature(false);
      setRevision((value) => value + 1);
    };
    const save = async (
      edit: PendingEdit,
      request: SetToothFullHeadMPRSliceEditorStateReq,
    ) => {
      const result = await dispatch(
        reportsModel.thunks.setToothFullHeadMPRSliceEditorState(request),
      );
      finish(
        edit,
        !reportsModel.thunks.setToothFullHeadMPRSliceEditorState.fulfilled.match(
          result,
        ),
      );
    };
    const onAnnotationsChanged = (
      event: FullHeadMPRUIEvents['fullHeadMPRAnnotationsChanged'],
    ) => {
      const target = targetsRef.current[event.projection];
      if (
        !latestRef.current.canEditAnnotations ||
        pendingRef.current ||
        !target ||
        target.id !== event.targetID
      ) {
        return;
      }
      const snapshot = viewer.getViewSnapshot(event.projection);
      if (
        !snapshot ||
        !isSameFullHeadMprAnnotationPlane({
          isSeries: target.isSeries,
          left: event.view,
          right: snapshot.view,
        })
      ) {
        return;
      }
      const annotations = [
        new AnnotationsLayer({
          Readonly: false,
          Annotations: formatAnnotations(event.annotations),
        }),
      ];
      const edit: PendingEdit = {
        projection: event.projection,
        target,
        annotations,
      };
      const request = makeFullHeadMprEditorStateRequest({
        original: target.original,
        slices: latestRef.current.slices,
        snapshot,
        toothID,
      });
      pendingRef.current = edit;
      setPending(edit);
      setIsCheckingSignature(true);
      latestRef.current.checkReportSignature({
        toothID,
        onCancel: () => finish(edit, true),
        onSignatureChecked: async () => {
          if (pendingRef.current !== edit) {
            return;
          }
          if (mountedRef.current) {
            setIsCheckingSignature(false);
          }
          await save(
            edit,
            new SetToothFullHeadMPRSliceEditorStateReq({
              ...request,
              Annotations: { Layers: annotations },
              SignatureRemovalConfirmed: true,
            }),
          );
        },
      });
    };
    const saveAll = async (
      edit: PendingEdit,
      requests: SetToothFullHeadMPRSliceEditorStateReq[],
    ) => {
      const rejected = await requests.reduce(async (previous, request) => {
        const wasRejected = await previous;
        const result = await dispatch(
          reportsModel.thunks.setToothFullHeadMPRSliceEditorState(request),
        );
        return (
          wasRejected ||
          !reportsModel.thunks.setToothFullHeadMPRSliceEditorState.fulfilled.match(
            result,
          )
        );
      }, Promise.resolve(false));
      finish(edit, rejected);
    };
    // A synced gesture reports one event per pane in the same tick; they are
    // collected and saved as one pending edit, or the single slot would drop
    // every pane but the first.
    const imageOptionsBatchRef: {
      current: FullHeadMPRUIEvents['fullHeadMPRImageOptionsChanged'][];
    } = { current: [] };
    const flushImageOptions = () => {
      const events = imageOptionsBatchRef.current;
      imageOptionsBatchRef.current = [];
      if (!latestRef.current.canEditImageOptions || pendingRef.current) {
        return;
      }
      const items = events.flatMap((event) => {
        const target = targetsRef.current[event.projection];
        const snapshot = viewer.getViewSnapshot(event.projection);
        return target &&
          target.id === event.targetID &&
          snapshot &&
          isSameFullHeadMprPlane(event.view, snapshot.view)
          ? [{ event, target }]
          : [];
      });
      const first = items[0];
      if (!first) {
        return;
      }
      const edit: PendingEdit = {
        projection: first.event.projection,
        target: first.target,
        options: first.event.options,
        optionsByProjection: Object.fromEntries(
          items.map(({ event, target }) => [
            event.projection,
            { options: event.options, targetID: target.id },
          ]),
        ),
      };
      const requests = items.map(
        ({ event, target }) =>
          new SetToothFullHeadMPRSliceEditorStateReq({
            ...makeFullHeadMprEditorStateRequest({
              original: target.original,
              slices: latestRef.current.slices,
              snapshot: { ...event, presentationID: null },
              toothID,
            }),
            ViewOptions: new MedicalImageViewOptions(
              transformViewOptions(event.options),
            ),
          }),
      );
      pendingRef.current = edit;
      setPending(edit);
      void saveAll(edit, requests);
    };
    const onImageOptionsChanged = (
      event: FullHeadMPRUIEvents['fullHeadMPRImageOptionsChanged'],
    ) => {
      if (imageOptionsBatchRef.current.length === 0) {
        queueMicrotask(flushImageOptions);
      }
      imageOptionsBatchRef.current.push(event);
    };
    const subscribe = <Event extends keyof FullHeadMPRUIEvents>(
      name: Event,
      listener: (event: FullHeadMPRUIEvents[Event]) => void,
    ) =>
      session
        ? session.addEventListener(name, listener)
        : MedicalImageRender.addEventListener(name, listener, 'fullHeadMPR');
    const disposers = [
      subscribe('fullHeadMPRViewUpdated', updateSnapshots),
      subscribe('fullHeadMPRViewportRendered', updateSnapshots),
      subscribe('fullHeadMPRAnnotationsPreview', (event) => {
        setPreviews((current) => ({ ...current, [event.projection]: event }));
      }),
      subscribe('fullHeadMPRAnnotationsChanged', onAnnotationsChanged),
      subscribe('fullHeadMPRImageOptionsChanged', onImageOptionsChanged),
    ];
    updateSnapshots();

    return () => {
      disposers.forEach((dispose) => dispose?.());
      FULL_HEAD_MPR_SESSION_PROJECTIONS.forEach((projection) => {
        void viewer.setAnnotationTarget(projection, null);
        viewer.setImageOptionsTarget(projection, null);
      });
      targetsRef.current = {};
      pendingRef.current = null;
    };
  }, [isReady, session, toothID, viewer]);

  useEffect(() => {
    FULL_HEAD_MPR_SESSION_PROJECTIONS.forEach((projection) => {
      const snapshot = viewer.getVisibleProjections().includes(projection)
        ? viewer.getViewSnapshot(projection)
        : null;
      if (!hasLoadedTooth || !snapshot) {
        void viewer.setAnnotationTarget(projection, null);
        viewer.setImageOptionsTarget(projection, null);
        return;
      }
      const target = makeFullHeadMprEditorTarget({
        original:
          pending &&
          isSameFullHeadMprAnnotationPlane({
            isSeries: pending.target.isSeries,
            left: pending.target.original.view,
            right: snapshot.view,
          })
            ? pending.target.original
            : original,
        previous: targetsRef.current[projection],
        slices,
        snapshot,
        toothID,
      });
      if (!target) {
        return;
      }
      targetsRef.current[projection] = target;
      const record = slices.find((slice) => slice.ID === target.recordID);
      const currentEdit = pending?.target.id === target.id ? pending : null;
      const geometry = target.original.geometry;
      const confirmedLayers =
        record?.MedicalImageFeatures?.Annotations?.Layers ?? [];
      void viewer.setAnnotationTarget(projection, {
        annotations: currentEdit?.annotations
          ? [
              ...confirmedLayers.filter((layer) => layer.Readonly),
              ...currentEdit.annotations,
            ]
          : confirmedLayers,
        heightPx: geometry.HeightPx,
        widthPx: geometry.WidthPx,
        id: target.id,
        isSeries: target.isSeries,
        readonly: !canEditAnnotations,
        view: target.original.view,
      });
      viewer.setImageOptionsTarget(projection, {
        id: target.id,
        readonly: !canEditImageOptions,
        view: snapshot.view,
        widthPx: snapshot.widthPx,
        heightPx: snapshot.heightPx,
      });
      const batchedEdit = pending?.optionsByProjection?.[projection];
      const options =
        (batchedEdit?.targetID === target.id ? batchedEdit.options : null) ??
        currentEdit?.options ??
        (record ? makeFullHeadMprSliceViewOptions(record) : null);
      const optionsKey = `${target.id}:${JSON.stringify(options)}`;
      if (appliedImageOptionsRef.current[projection] !== optionsKey) {
        appliedImageOptionsRef.current[projection] = optionsKey;
        viewer.setImageOptions(projection, options);
      }
    });
  }, [
    canEditAnnotations,
    canEditImageOptions,
    hasLoadedTooth,
    original,
    pending,
    revision,
    session,
    slices,
    snapshots,
    toothID,
    viewer,
  ]);

  const panes = FULL_HEAD_MPR_SESSION_PROJECTIONS.flatMap((projection) => {
    const snapshot = snapshots[projection];
    const element = viewer.getPaneElement(projection);
    const surface = viewer.getPaneSurface(projection);
    if (!snapshot || !element || !surface) {
      return [];
    }
    const isPaneReady = readyProjections.includes(projection);
    const target = targetsRef.current[projection];
    const preview = previews[projection];
    const annotations =
      isPaneReady &&
      target &&
      preview &&
      !preview.isRenderedByEngine &&
      preview.targetID === target.id &&
      isSameFullHeadMprAnnotationPlane({
        isSeries: target.isSeries,
        left: preview.view,
        right: snapshot.view,
      })
        ? transformFullHeadMprGraphicsAnnotations({
            annotations: preview.annotations,
            transform: makeFullHeadMprPointTransform({
              from: preview,
              to: snapshot,
            }),
          })
        : [];
    return [
      {
        annotations,
        element,
        isReady: isPaneReady,
        projection,
        snapshot,
        surface,
      },
    ];
  });

  const hasAnnotationsByProjection = FULL_HEAD_MPR_SESSION_PROJECTIONS.reduce<
    Partial<Record<FullHeadMPRProjection, boolean>>
  >((result, projection) => {
    const snapshot = snapshots[projection];
    const target = targetsRef.current[projection];
    if (
      !snapshot ||
      !target ||
      !isSameFullHeadMprAnnotationPlane({
        isSeries: target.isSeries,
        left: target.original.view,
        right: snapshot.view,
      })
    ) {
      return result;
    }
    const record = slices.find((slice) => slice.ID === target.recordID);
    const confirmedLayers =
      record?.MedicalImageFeatures?.Annotations?.Layers ?? [];
    const currentEdit = pending?.target.id === target.id ? pending : null;
    const layers = currentEdit?.annotations
      ? [
          ...confirmedLayers.filter((layer) => layer.Readonly),
          ...currentEdit.annotations,
        ]
      : confirmedLayers;
    const preview = previews[projection];
    const hasPreviewAnnotations =
      preview?.targetID === target.id &&
      isSameFullHeadMprAnnotationPlane({
        isSeries: target.isSeries,
        left: preview.view,
        right: snapshot.view,
      }) &&
      preview.annotations.length > 0;

    return {
      ...result,
      [projection]:
        layers.some((layer) => layer.Annotations.length > 0) ||
        Boolean(hasPreviewAnnotations),
    };
  }, {});

  const isDownloadDisabled = !isReady || isSaving || !!pending || isDownloading;
  const download = async () => {
    if (isDownloadDisabled || downloadingRef.current) {
      return;
    }

    const activePane = viewer.getActivePane();
    if (!activePane || !viewer.isViewReady(activePane.projection)) {
      return;
    }

    downloadingRef.current = true;
    setIsDownloading(true);
    try {
      const surface = viewer.getPaneSurface(activePane.projection);
      const overlay = surface ? captureAnnotationsOverlay(surface) : null;
      const image = await viewer.captureScreenshot(activePane.projection);
      if (!image) {
        throw new Error('Unable to capture the MPR image');
      }
      const composed = await composeAnnotationsOverlay({ image, overlay });
      const url = URL.createObjectURL(composed);
      try {
        const link = document.createElement('a');
        const series =
          FULL_HEAD_MPR_PROJECTION_TO_SERIES[activePane.projection];
        link.href = url;
        link.download = `${formatMessage(medicalImageTitles[TOOTH_BASIS_SERIES_TO_GROUP[series]])}.png`;
        link.click();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch {
      toastCaller({
        type: 'error',
        heading: formatMessage(toastErrors.errorHeading),
        message: formatMessage(toastErrors.defaultError),
        autoClose: 10000,
      });
    } finally {
      downloadingRef.current = false;
      if (mountedRef.current) {
        setIsDownloading(false);
      }
    }
  };

  return {
    canEditAnnotations,
    canEditImageOptions,
    download,
    hasAnnotationsByProjection,
    isCheckingSignature,
    isDownloadDisabled,
    panes,
    snapshots,
  };
};
